import React from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { useComplianceRequestsStore } from '../model';
import { ComplianceBar } from './ComplianceBar';

export const ComplianceStack: React.FC<{ paymentId?: string }> = ({
  paymentId,
}) => {
  const { needActionPayments, shouldAutoOpenComplianceModal } =
    useComplianceRequestsStore();

  if (!needActionPayments?.length) return null;
  const autoOpenPaymentId = shouldAutoOpenComplianceModal
    ? needActionPayments[0]?.id
    : undefined;

  if (paymentId) {
    return (
      <ComplianceBar
        paymentId={paymentId}
        autoOpen={paymentId === autoOpenPaymentId}
      />
    );
  }

  if (needActionPayments.length === 1) {
    return (
      <ComplianceBar
        paymentId={needActionPayments[0].id}
        autoOpen={needActionPayments[0].id === autoOpenPaymentId}
      />
    );
  }

  const visibleItems = needActionPayments.slice(0, 2);

  return (
    <div className="flex w-full items-center justify-center">
      <div className="relative h-[96px] w-full md:max-w-[630px]">
        <AnimatePresence mode="popLayout">
          {visibleItems.map((item, index) => {
            const isFront = index === 0;

            return (
              <StackedComplianceBar
                key={item.id}
                paymentId={item.id}
                isFront={isFront}
                autoOpen={item.id === autoOpenPaymentId}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

function StackedComplianceBar({
  paymentId,
  isFront,
  autoOpen,
}: {
  paymentId: string;
  isFront: boolean;
  autoOpen: boolean;
}) {
  return (
    <motion.div
      layout
      initial={isFront ? 'front' : 'back'}
      animate={isFront ? 'front' : 'back'}
      exit="exit"
      variants={{
        front: {
          scale: 1,
          y: 0,
          opacity: 1,
          zIndex: 10,
          filter: 'blur(0px)',
          transition: { type: 'spring', stiffness: 300, damping: 25 },
        },
        back: {
          scale: 0.96,
          y: 12,
          opacity: 0.55,
          zIndex: 0,
          filter: 'blur(0px)',
          transition: { duration: 0.4 },
        },
        exit: {
          opacity: 0,
          scale: 1.02,
          y: -10,
          filter: 'blur(4px)',
          transition: { duration: 0.4, ease: 'easeInOut' },
        },
      }}
      className="absolute left-0 top-0 w-full origin-bottom"
    >
      <ComplianceBar paymentId={paymentId} autoOpen={autoOpen} />
      {!isFront && (
        <div className="absolute inset-0 rounded-[24px] bg-black/30" />
      )}
    </motion.div>
  );
}
